import { AlertTriangle } from 'lucide-react';
import { ApplyTemplatePanel } from '../../../../components/person/ApplyTemplatePanel';
import { OnboardingChecklistCard } from '../../../../components/onboarding/OnboardingChecklistCard';
import { usePersonOnboarding } from '../../../../hooks/usePersonOnboarding';

/**
 * Step 2. Creating the person already applies their role template, so this
 * step is a confirmation of what that produced rather than an action: it
 * answers "did picking that role actually do anything?" before HR starts
 * ticking extras. Re-apply is there for a role set after the fact.
 */
export function TemplateStep({
  personId,
  position,
}: {
  personId: string;
  position: string | null;
}) {
  const onboarding = usePersonOnboarding(personId);
  // Only trust a zero once the query has actually answered: a failed fetch
  // also leaves data undefined, and claiming "nothing was assigned" then
  // would send HR off rebuilding a list that already exists.
  const assignedNothing = !!onboarding.data && onboarding.data.total === 0;

  return (
    <div className="space-y-4">
      {!position ? (
        <Warning>
          No job role was set, so no template could be applied. Pick a role on
          their record if one applies, then re-apply below. Otherwise assign
          everything by hand over the next few steps.
        </Warning>
      ) : assignedNothing ? (
        <Warning>
          Nothing was assigned for <strong>{position}</strong>. That role has no
          onboarding template yet, so build their list by hand over the next few
          steps, or set the template up under Onboarding templates.
        </Warning>
      ) : null}

      <OnboardingChecklistCard personId={personId} />
      <ApplyTemplatePanel personId={personId} position={position} />
    </div>
  );
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-token border border-warning/20 bg-warning-soft/15 px-4 py-3 text-sm text-warning-soft-fg">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
